import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { Route, Router, ActivatedRoute, Params } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CountryMapService } from "../../services/countrymap.service";
import { CountryMapZoom } from "../../interfaces/icountrymap";
import { IDashboard, IDropDown, IDropDownItem } from "../../interfaces/idashboard";
import { IBusinessFunction } from "../../interfaces/ibusinessfunction";
import { LatestUpdate } from "../../models/latestupdate";
import { DashboardService } from "../../services/dashboard.service";
import { LatestUpdateService } from "../../services/latestupdate.service";
import { AuthService } from "../../services/auth.service";
import { LatestUpdateType } from "../../models/latestupdate";
import { BusinessFunctionService } from "../../services/businessfunction.service";
import { RoutesService } from "../../services/routes.service";
import { Features } from "../../enums/features";
import { Title } from "@angular/platform-browser";
import { ICountry } from "../../interfaces/icountry";
import { ToastrService } from "ngx-toastr";

@Component({
    selector: "home",
    templateUrl: "./home.component.html",
    providers: [CountryMapService],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    countryMapFilter: CountryMapZoom = new CountryMapZoom();
    updates!: LatestUpdate[];
    pageNumber: number = 1;
    pageSize = 10;
    showReadMore: boolean = false;
    lastUpdatedDate: Date | null = null;
    defaultDate: Date = new Date(1, 1, 1);
    mapDescriptions: string[] = new Array();
    mapKey!: IDropDown;
    businessFunction!: IBusinessFunction | null;
    routes: any[] = new Array();
    mapRouteLink: string = "";
    features = Features;
    DashboardExists = false;
    updatesCountPerPage: any;
    totalUpdateCount: any;
    hasSubscription: boolean = false;
    modalCountries: ICountry[] = [];
    countriesWithCategoryDetail!: ICountry[];
    countriesForPublicPortal!: ICountry[];
    public latestUpdateType = LatestUpdateType;

    @ViewChild("subscription") emailSubscription!: any;

    constructor(private activatedRoute: ActivatedRoute,
        private countryMapService: CountryMapService,
        private dashboardService: DashboardService,
        private latestUpdateService: LatestUpdateService,
        private businessFunctionservice: BusinessFunctionService,
        private authService: AuthService,
        private router: Router,
        private routesService: RoutesService,
        private titleService: Title,
        private toastr: ToastrService
    ) {
        // Browser tab title is set centrally in AppComponent on NavigationEnd
        // (PortalName | PageName). Avoid forcing the literal route data.title
        // here, which previously caused the tab to display "Default Home Page".
        this.routesService.currentPageTitle.next('Default Home');
    }

    ngOnInit(): void {
        let dashboard: IDashboard = this.activatedRoute.snapshot.data["dashboard"] as IDashboard;

        this.activatedRoute.params
            .pipe(takeUntil(this.destroy$))
            .subscribe((params: Params) => {
                this.countriesWithCategoryDetail = this.activatedRoute.snapshot.data['countriesWithCategoryDetail'];
                this.countriesForPublicPortal = this.activatedRoute.snapshot.data['countriesForPublicPortal'];
            });

        if (dashboard) {
            this.DashboardExists = true;
            let dropDownsToRender: any = this.dashboardService.getDropDownsToRender(dashboard.dropDowns, null, null);
            this.countryMapFilter.countryMap = this.dashboardService.getDataForMap(dropDownsToRender, null);
            this.countryMapService.announceDropDownsChanged(this.countryMapFilter);
            this.latestUpdateService.getUpdates(this.pageNumber, this.pageSize)
                .pipe(takeUntil(this.destroy$))
                .subscribe(i => {
                    if (i.length < 10) {
                        this.updates = i;
                        this.showReadMore = false;
                    }
                    else {
                        this.updates = i;
                        this.setReadMoreButtonVisibility();
                    }
                });
            this.businessFunction = AuthService.businessFunction;
            if (this.businessFunction) {
                this.lastUpdatedDate = this.businessFunction.lastUpdatedDate;
                let sub = this.businessFunction.features.filter(x => x.featureId == Features.EmailDigest);
                this.hasSubscription = sub.length > 0 ? sub[0].enabled : false;
                this.modalCountries = this.businessFunction.authenticationRequired ?
                    (this.countriesWithCategoryDetail.length > 0 ? this.countriesWithCategoryDetail : [])
                    : (this.countriesForPublicPortal.length > 0 ? this.countriesForPublicPortal : []);
            }



            // set map description
            if (dropDownsToRender.length === 1 && dropDownsToRender[0].presentationTypeId === 2) {
                // we only have one item which is a key item
                this.mapDescriptions.push(dropDownsToRender[0].label);
            } else {
                // we have dropdown options - so show the hierarchy of selected items
                dropDownsToRender.forEach((dropDown: IDropDown) => {
                    if (dropDown.presentationTypeId === 1) {
                        var dropDownItem: any = dropDown.dropDownItems.filter((item: IDropDownItem) => { return item.id === dropDown.selectedDropDownItemId; })[0];
                        this.mapDescriptions.push(dropDownItem.text);
                    }
                });
            }

            this.mapKey = dropDownsToRender.filter((dropdown: IDropDown) => {
                return dropdown.presentationTypeId === 2;
            })[0];

            this.getMapRoute();
        }
    }

    /**
     * Is called when read more is clicked. Loads more news items
     */
    onReadMoreClicked(): void {
        this.pageNumber++;
        this.latestUpdateService.getUpdates(this.pageNumber, this.pageSize)
            .pipe(takeUntil(this.destroy$))
            .subscribe(i => {
                if (i.length > 0) {
                    this.updatesCountPerPage = i.length;
                    this.updates = this.updates.concat(i);
                    this.setReadMoreButtonVisibility();
                } else {
                    this.showReadMore = false;
                }
            });
    }

    /*
    * Set the visibility of the read more button
    */
    setReadMoreButtonVisibility(): void {
        this.latestUpdateService.getTotalUpdates()
            .pipe(takeUntil(this.destroy$))
            .subscribe(i => { this.totalUpdateCount = i; });
        if (this.updatesCountPerPage < 10 || this.totalUpdateCount == this.updates.length) {
            this.showReadMore = false;
        }
        else {
            this.showReadMore = true;
        }
    }

    /*
    * Get map route
    */
    getMapRoute(): void {
        this.routes = this.routesService.getNavigationRoutes(this.router, AuthService.businessFunction);
        this.routes.forEach((r: Route) => {
            if (r.data && r.path && AuthService.businessFunction) {
                r.data["nav"].navPath = r.path.replace(":defaultroute", AuthService.businessFunction.defaultRoute);
            }
        });
        const mapRoute = this.routes.find((r: Route) => r.data && r.data["nav"].text === "Map");
        this.mapRouteLink = mapRoute?.data?.["nav"]?.navPath || "";
    }

    onShowSubscription($event: any): void {
        this.emailSubscription.Open();
    }

    onFormSubmit(message: string) {
        this.toastr.success(message);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Helper method to check if last updated date should be shown
    shouldShowLastUpdatedDate(): boolean {
        if (!this.lastUpdatedDate) return false;
        const lastYear = new Date(this.lastUpdatedDate).getFullYear();
        const defaultYear = this.defaultDate.getFullYear();
        return lastYear > defaultYear;
    }
}